import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './Navbar.css';

const Navbar: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Handle scroll for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto scroll to top when location changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Close mobile menu on location change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  }, [location.pathname]);

  // Close mobile menu on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 992) {
        setIsMobileMenuOpen(false);
        document.body.style.overflow = 'unset';
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleMobileMenu = () => {
    const newState = !isMobileMenuOpen;
    setIsMobileMenuOpen(newState);
    document.body.style.overflow = newState ? 'hidden' : 'unset';
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  };

  return (
    <>
      {/* Top Info Bar */}
      <div className="top-info-bar">
        <div className="container">
          <div className="info-left">
            <span className="info-item">
              <i className="icon-clock"></i>
              Mon, Wed, Thurs, Fri: 10AM – 7PM
            </span>
            <span className="info-item">
              <i className="icon-phone"></i>
              (720) 773-5733
            </span>
            <span className="info-item">
              <i className="icon-map-marker"></i>
              Littleton, CO 80123
            </span>
          </div>
          <div className="info-right">
            <a href="https://www.facebook.com" target="_blank" rel="noopener noreferrer">
              <i className="icon-facebook"></i>
            </a>
            <a href="https://www.instagram.com" target="_blank" rel="noopener noreferrer">
              <i className="icon-instagram"></i>
            </a>
            <a href="https://www.tiktok.com" target="_blank" rel="noopener noreferrer">
              <i className="icon-tiktok"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className={`main-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          {/* Logo */}
          <Link to="/" className="logo" onClick={closeMobileMenu}>
            <img src={require('../assets/logo.png')} alt="VitalBalance Hormones" />
          </Link>

          {/* Desktop Navigation */}
          <div className="desktop-nav">
            <NavLink to="/" end className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              Home
            </NavLink>
            <NavLink to="/about" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              About
            </NavLink>
            <NavLink to="/services" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              Services
            </NavLink>
            <NavLink to="/blog" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              Blog
            </NavLink>
            <div className="booking-badge">
              <NavLink to="/booking" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
                Booking
              </NavLink>
              <span className="badge-dot"></span>
            </div>
            <NavLink to="/contact" className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}>
              Contact
            </NavLink>
            <Link to="/booking" className="cta-button">
              <i className="icon-calendar"></i>
              Schedule Consultation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>

        {/* Mobile Navigation Overlay */}
        <div className={`mobile-nav-overlay ${isMobileMenuOpen ? 'active' : ''}`}>
          <div className="mobile-nav">
            <NavLink 
              to="/" 
              end 
              className={({ isActive }) => isActive ? 'mobile-nav-link active' : 'mobile-nav-link'}
              onClick={closeMobileMenu}
            >
              Home
            </NavLink>
            <NavLink 
              to="/about" 
              className={({ isActive }) => isActive ? 'mobile-nav-link active' : 'mobile-nav-link'}
              onClick={closeMobileMenu}
            >
              About
            </NavLink>
            <NavLink 
              to="/services" 
              className={({ isActive }) => isActive ? 'mobile-nav-link active' : 'mobile-nav-link'}
              onClick={closeMobileMenu}
            >
              Services
            </NavLink>
            <NavLink 
              to="/blog" 
              className={({ isActive }) => isActive ? 'mobile-nav-link active' : 'mobile-nav-link'}
              onClick={closeMobileMenu}
            >
              Blog
            </NavLink>
            <div className="mobile-booking-badge">
              <NavLink 
                to="/booking" 
                className={({ isActive }) => isActive ? 'mobile-nav-link active' : 'mobile-nav-link'}
                onClick={closeMobileMenu}
              >
                Booking
              </NavLink>
              <span className="mobile-badge-dot"></span>
            </div>
            <NavLink 
              to="/contact" 
              className={({ isActive }) => isActive ? 'mobile-nav-link active' : 'mobile-nav-link'}
              onClick={closeMobileMenu}
            >
              Contact
            </NavLink>
            <Link 
              to="/booking" 
              className="mobile-cta-button"
              onClick={closeMobileMenu}
            >
              <i className="icon-calendar"></i>
              Schedule Consultation
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navbar;